import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import moment from "moment";
import useAxiosFetch from "../common/useAxiosFetch";
import { useAppContext } from "../AppContext";
import useDismiss from "../shell/useDismiss";

const RING_RADIUS = 15;
const RING_CIRCUMFERENCE = 2 * Math.PI * RING_RADIUS;

const calculatePercentage = (current, limit) => {
  if (limit === -1 || !limit) return 0;
  return Math.min((current / limit) * 100, 100);
};

const getLevel = (percentage) => {
  if (percentage >= 95) return "high";
  if (percentage >= 80) return "medium";
  return "low";
};

const UsageRow = ({ label, icon, current, limit, children }) => {
  const percentage = calculatePercentage(current, limit);
  const level = getLevel(percentage);
  return (
    <div className="usage-row">
      <div className="usage-row-head">
        <span>{icon && <i className={icon} />}{label}</span>
        <span className={`usage-row-count ${limit !== -1 ? level : ""}`}>
          {current} / {limit === -1 ? "∞" : limit}
        </span>
      </div>
      {limit !== -1 && (
        <div className="usage-bar">
          <div className={`usage-bar-fill ${level}`} style={{ width: `${percentage}%` }} />
        </div>
      )}
      {children}
    </div>
  );
};

const UsageIndicator = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { currentUser } = useAppContext();
  const [showDropdown, setShowDropdown] = useState(false);
  const wrapperRef = useRef(null);
  const dismissRefs = useMemo(() => [wrapperRef], []);
  const close = useCallback(() => setShowDropdown(false), []);
  useDismiss(showDropdown, dismissRefs, close);

  const { data: usage, refetch } = useAxiosFetch("/api/features/limits");

  useEffect(() => {
    if (!currentUser) return undefined;
    const interval = setInterval(() => refetch(), 30000);
    return () => clearInterval(interval);
  }, [currentUser, refetch]);

  if (!usage) return null;

  const { limits } = usage;
  const hasAnyLimit =
    limits.maxOrganizations !== -1 ||
    limits.maxProjects !== -1 ||
    limits.maxUsers !== -1 ||
    limits.aiCallsPerWeek !== -1;

  if (!hasAnyLimit) return null;

  const percentages = [
    calculatePercentage(usage.usage.organizations.current, limits.maxOrganizations),
    calculatePercentage(usage.usage.projects.current, limits.maxProjects),
    calculatePercentage(usage.usage.users.current, limits.maxUsers),
    calculatePercentage(usage.usage.aiCalls.current, limits.aiCallsPerWeek),
  ];
  const highest = Math.max(...percentages);
  const level = getLevel(highest);

  return (
    <div className="usage" ref={wrapperRef}>
      <button
        type="button"
        className={`topbar-btn usage-ring ${level} ${showDropdown ? "open" : ""}`}
        onClick={() => setShowDropdown(!showDropdown)}
        title={t("Feature Usage")}
        aria-label={t("Feature Usage")}
        aria-expanded={showDropdown}
      >
        <svg viewBox="0 0 36 36" aria-hidden="true">
          <circle className="usage-ring-track" cx="18" cy="18" r={RING_RADIUS} />
          <circle
            className="usage-ring-value"
            cx="18"
            cy="18"
            r={RING_RADIUS}
            strokeDasharray={RING_CIRCUMFERENCE}
            strokeDashoffset={RING_CIRCUMFERENCE * (1 - Math.max(highest, 4) / 100)}
          />
        </svg>
        <i className="icon-statistics" />
      </button>

      {showDropdown && (
        <div className="usage-pop">
          <div className="usage-pop-title">{t("Feature Usage")}</div>
          <UsageRow label={t("organizations")} current={usage.usage.organizations.current} limit={limits.maxOrganizations} />
          <UsageRow label={t("domains")} current={usage.usage.projects.current} limit={limits.maxProjects} />
          <UsageRow label={t("users")} current={usage.usage.users.current} limit={limits.maxUsers} />
          <UsageRow
            label={t("AI Calls This Week")}
            icon="icon-stars1"
            current={usage.usage.aiCalls.current}
            limit={limits.aiCallsPerWeek}
          >
            {limits.aiCallsPerWeek !== -1 && (
              <div className="usage-row-hint">
                {t("Resets")}: {moment(usage.usage.aiCalls.resetsAt).format("D MMM")}
              </div>
            )}
          </UsageRow>
          <button
            type="button"
            className="usage-pop-link"
            onClick={() => {
              close();
              navigate("/subscriptions");
            }}
          >
            {t("View Details")} <i className="icon-arrow-right" />
          </button>
        </div>
      )}
    </div>
  );
};

export default UsageIndicator;
